import { channelSelectionFromSuggestion, toggleSelectedChannel } from "@/api/channelSelection";
import {
  getAtPath,
  IMAGE_SIZE_OVERRIDE_KEY,
  parseListOrScalar,
  parseOverrideKey,
  parseRecipe,
} from "./recipe";
import { patchOverridesIntoYaml } from "./recipe/recipeYamlPatch";
import type { KeyValuePair, SubmitFormState } from "./submitWizardForm";
import {
  emptySigmaDefaultsState,
  SIGMA_WRISTS,
  SIGMA_LEGACY_WRISTS,
  SIGMA_CAMERA_PATH,
  type SigmaDatasetDefaults,
  type SigmaDefaultsState,
} from "./sigmaDatasetDefaults";

const IMAGE_SIZE = { height: 238, width: 294 };

function sameValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function touchesPath(row: KeyValuePair, path: string): boolean {
  const rowPath = parseOverrideKey(row.key.trim()).path;
  return rowPath === path || path.startsWith(`${rowPath}.`);
}

// 부모 객체를 교체한 수동 override도 그 아래의 자동값보다 우선한다.
function hasManualRow(rows: KeyValuePair[], path: string): boolean {
  return rows.some((row) => touchesPath(row, path));
}

export function reconcileSigmaDefaults(
  form: SubmitFormState,
  defaults: SigmaDatasetDefaults,
): SubmitFormState {
  if (!defaults.applicable && form.sigmaDefaults === undefined) return form;
  const previous = form.sigmaDefaults ?? emptySigmaDefaultsState();
  if (
    !defaults.applicable &&
    previous.imageSize !== "automatic" &&
    previous.observer !== "automatic" &&
    !Object.values(previous.wrists).some((wrist) => wrist !== "manual") &&
    !Object.values(previous.removedWrists).some((wrist) => wrist !== "manual")
  )
    return form;
  const state: SigmaDefaultsState = {
    ...previous,
    wrists: { ...previous.wrists },
    removedWrists: { ...previous.removedWrists },
  };
  const config = { ...form.config };
  let selection = form.channelSelection;
  const source = parseRecipe(config.inlineYaml);
  const edited = config.editedYaml === null ? null : parseRecipe(config.editedYaml);
  const ready = defaults.applicable && config.configName !== "" && config.inlineYaml !== "";

  const editedImage =
    edited?.errors.length === 0 ? getAtPath(edited.data, IMAGE_SIZE_OVERRIDE_KEY) : undefined;
  const imageEdited =
    edited?.errors.length === 0 &&
    !sameValue(editedImage, getAtPath(source.data, IMAGE_SIZE_OVERRIDE_KEY));
  if (
    state.imageSize === "automatic" &&
    (imageEdited || hasManualRow(config.overrides, IMAGE_SIZE_OVERRIDE_KEY))
  ) {
    config.imageSize = null;
    state.imageSize = imageEdited ? "yaml" : "manual";
  } else if (state.imageSize === "automatic" && !sameValue(config.imageSize, IMAGE_SIZE)) {
    state.imageSize = "manual";
  }
  if (
    state.imageSize === null &&
    (config.imageSize !== null ||
      imageEdited ||
      hasManualRow(config.overrides, IMAGE_SIZE_OVERRIDE_KEY))
  ) {
    state.imageSize = imageEdited ? "yaml" : "manual";
  }
  if (ready && state.imageSize === null) {
    config.imageSize = IMAGE_SIZE;
    state.imageSize = "automatic";
  } else if (!ready && state.imageSize === "automatic") {
    config.imageSize = null;
    state.imageSize = null;
  }

  // fine-tune 부분 config에는 mux가 없을 수 있다. 제안 요청이 끝난 뒤 알려진 카메라만 채운다.
  // 벡터·action의 위임 상태와 사용자의 명시적인 빈 선택은 유지한다.
  if (
    ready &&
    state.suggestionReady &&
    defaults.cameraNames !== null &&
    defaults.cameraNames.length > 0 &&
    (selection === null || selection.observation_image === null)
  ) {
    selection = {
      ...(selection ??
        channelSelectionFromSuggestion({ observation_image: defaults.cameraNames })!),
      observation_image: defaults.cameraNames,
    };
  }
  const names = selection?.observation_image;
  if (names !== null && names !== undefined) {
    if (ready && typeof state.observer !== "boolean" && names.includes("observer")) {
      selection = toggleSelectedChannel(selection!, "observation_image", "observer");
      state.observer = "automatic";
    } else if (!ready && state.observer === "automatic") {
      // Dataset 변경 시 선택은 null로 돌아가고 새 Dataset의 제안으로 복원된다.
      if (defaults.cameraNames?.includes("observer") && !names.includes("observer")) {
        selection = toggleSelectedChannel(selection!, "observation_image", "observer");
      }
      state.observer = null;
    } else if (
      ready &&
      typeof state.observer === "boolean" &&
      defaults.cameraNames?.includes("observer")
    ) {
      if (names.includes("observer") !== state.observer)
        selection = toggleSelectedChannel(selection!, "observation_image", "observer");
    }
  } else if (!ready && state.observer === "automatic") {
    state.observer = null;
  }

  // 파싱할 수 없는 편집본은 기존 검증이 제출을 막는다. 원래 값을 보존하려고 복원도 보류한다.
  if (edited === null || edited.errors.length === 0) {
    for (const wrist of SIGMA_LEGACY_WRISTS) {
      const path = `${SIGMA_CAMERA_PATH}.${wrist}`;
      const tracked = state.removedWrists[wrist];
      if (tracked === "manual") continue;
      const rows = config.overrides.filter((row) => touchesPath(row, path));
      const isAutomaticRow = (row: KeyValuePair) =>
        row.key.trim() === `~${path}` && row.value === "";
      if (
        tracked !== undefined &&
        rows.some((row) => parseOverrideKey(row.key.trim()).path !== path)
      ) {
        config.overrides = config.overrides.filter((row) => !isAutomaticRow(row));
        state.removedWrists[wrist] = "manual";
        continue;
      }
      const originalValue = getAtPath(source.data, path);
      const currentValue = edited === null ? originalValue : getAtPath(edited.data, path);
      let manual: boolean;
      if (tracked === undefined) {
        manual = rows.length > 0 || (edited !== null && !sameValue(currentValue, originalValue));
      } else if (edited === null) {
        manual = rows.length !== 1 || !isAutomaticRow(rows[0]);
      } else {
        // 자동 삭제는 부모 객체를 남기므로, 부모까지 없으면 직접 삭제한 것으로 본다.
        manual =
          currentValue !== undefined || getAtPath(edited.data, SIGMA_CAMERA_PATH) === undefined;
      }
      if (manual) {
        state.removedWrists[wrist] = "manual";
        continue;
      }
      if (!ready || originalValue === undefined) {
        if (tracked === undefined) continue;
        if (config.editedYaml !== null) {
          config.editedYaml = patchOverridesIntoYaml(config.editedYaml, {
            [`++${path}`]: tracked.originalYamlValue,
          });
        } else {
          config.overrides = config.overrides.filter((row) => !isAutomaticRow(row));
        }
        delete state.removedWrists[wrist];
        continue;
      }
      state.removedWrists[wrist] =
        edited !== null && tracked !== undefined ? tracked : { originalYamlValue: originalValue };
      if (config.editedYaml !== null) {
        config.editedYaml = patchOverridesIntoYaml(config.editedYaml, { [`~${path}`]: "" });
      } else if (tracked === undefined) {
        config.overrides = [...config.overrides, { key: `~${path}`, value: "" }];
      }
    }
    for (const wrist of SIGMA_WRISTS) {
      const path = `${SIGMA_CAMERA_PATH}.${wrist}`;
      let tracked = state.wrists[wrist];
      const rows = config.overrides.filter((row) => touchesPath(row, path));
      if (
        tracked !== undefined &&
        tracked !== "manual" &&
        rows.some((row) => parseOverrideKey(row.key.trim()).path !== path)
      ) {
        // 상위 override와 충돌하는 자동 행만 제거한다. 값이나 연산을 바꾼 수동 행은 보존한다.
        const automaticShape = tracked.shape;
        config.overrides = config.overrides.filter(
          (row) =>
            row.key.trim() !== `++${path}` ||
            !sameValue(parseListOrScalar(row.value), automaticShape),
        );
        state.wrists[wrist] = "manual";
        continue;
      }
      const row =
        rows.length === 1 && parseOverrideKey(rows[0].key.trim()).path === path
          ? rows[0]
          : undefined;
      const currentValue =
        edited === null
          ? row && parseOverrideKey(row.key.trim()).op !== "delete"
            ? parseListOrScalar(row.value)
            : undefined
          : getAtPath(edited.data, path);
      if (
        tracked !== undefined &&
        tracked !== "manual" &&
        !sameValue(currentValue, tracked.shape)
      ) {
        tracked = "manual";
        state.wrists[wrist] = tracked;
      }
      if (tracked === "manual") continue;
      const chosen = selection?.observation_image;
      const shape =
        ready && (chosen === null || chosen === undefined || chosen.includes(wrist))
          ? defaults.shapes[wrist]
          : undefined;
      if (
        tracked === undefined &&
        (rows.length > 0 ||
          (edited !== null && !sameValue(currentValue, getAtPath(source.data, path))))
      ) {
        state.wrists[wrist] = "manual";
        continue;
      }
      if (tracked === undefined && shape === undefined) continue;
      if (shape === undefined) {
        if (tracked !== undefined) {
          if (config.editedYaml !== null) {
            config.editedYaml = patchOverridesIntoYaml(config.editedYaml, {
              [tracked.originalYamlValue === undefined ? `~${path}` : `++${path}`]:
                tracked.originalYamlValue,
            });
          } else {
            config.overrides = config.overrides.filter((item) => item !== row);
          }
          delete state.wrists[wrist];
        }
      } else {
        state.wrists[wrist] = {
          shape,
          originalYamlValue:
            edited !== null && tracked !== undefined
              ? tracked.originalYamlValue
              : getAtPath(source.data, path),
        };
        if (config.editedYaml !== null) {
          config.editedYaml = patchOverridesIntoYaml(config.editedYaml, { [`++${path}`]: shape });
        } else {
          config.overrides = [
            ...config.overrides.filter((item) => item !== row),
            { key: `++${path}`, value: JSON.stringify(shape) },
          ];
        }
      }
    }
  }
  if (
    sameValue(form.config, config) &&
    sameValue(form.channelSelection, selection) &&
    sameValue(previous, state)
  )
    return form;
  return { ...form, config, channelSelection: selection, sigmaDefaults: state };
}

// config 로드가 실패해도 이전 config의 자동 override가 새 config에 남지 않게 한다.
// 직접 편집 전의 override 스냅샷을 복원한 뒤 호출한다.
export function resetSigmaConfigDefaults(form: SubmitFormState): SubmitFormState {
  const restored = reconcileSigmaDefaults(
    { ...form, config: { ...form.config, editedYaml: null } },
    { applicable: false, cameraNames: null, shapes: {}, warnings: [] },
  );
  return {
    ...restored,
    sigmaDefaults:
      form.sigmaDefaults === undefined
        ? undefined
        : { ...emptySigmaDefaultsState(), observer: form.sigmaDefaults.observer },
  };
}
